import React, { useState } from 'react';
import { AuthProvider, useAuth } from './context/FirebaseAuthContext';
import { GameSessionProvider, useGameSession } from './context/GameSessionContext';
import { GameProvider } from './context/GameContext';
import { Grid } from './components/Grid';
import { HUD } from './components/HUD';
import { PatientForm } from './components/PatientForm';
import { ResultsDashboard } from './components/ResultsDashboard';
import { CompletionModal } from './components/CompletionModal';
import UserDashboard from './components/UserDashboard';
import AuthForm from './components/AuthForm';
import PatientGameSelector from './components/PatientGameSelector';
import AdminDashboard from './components/AdminDashboard';
import { useGame } from './context/GameContext';
import { useDDA } from './hooks/useDDA';
import { useGameEngine } from './hooks/useGameEngine';
import { storage } from './utils/storage';
import { GameResultSaver } from './utils/firebaseGameResultSaver';
import { LevelData } from './types';
import { MemoryGame } from './games/memory/MemoryGame';
import ToneGame from './games/tone/ToneGame';
import { ToneGameProvider } from './games/tone/ToneGameContext';
import MatchingGrid from './games/matching-grid/MatchingGrid';
import { MatchingGridProvider } from './games/matching-grid/MatchingGridContext';
import { TickerTapeGame } from './games/ticker-tape/TickerTapeGame';

type GameType = 'sequence' | 'memory' | 'tone' | 'matching' | 'ticker';

// Helper function to normalize game types
const normalizeGameType = (gameType: string): GameType => {
  switch (gameType) {
    case 'NumberSequence':
      return 'sequence';
    case 'MemoryPalace':
      return 'memory';
    case 'ToneMemory':
      return 'tone';
    case 'ImageMatching':
      return 'matching';
    case 'TickerTape':
      return 'ticker';
    default:
      return gameType as GameType;
  }
};

const GameContent: React.FC = () => {
  const { state, dispatch } = useGame();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { currentSession, isSessionActive, endSession } = useGameSession();
  const { updatePerformance } = useDDA();
  const { calculateScore } = useGameEngine();
  const [showCompletionModal, setShowCompletionModal] = React.useState(false);
  const [gameType, setGameType] = useState<GameType>('sequence');
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [showGameSelector, setShowGameSelector] = useState(true); // Control when to show game selector

  // Handle level completion - Move useEffect before any conditional returns
  React.useEffect(() => {
    if (state.isComplete && !showCompletionModal && isAuthenticated) {
      const score = calculateScore();
      const accuracy = state.selectedCells.length > 0
        ? (state.selectedCells.length / (state.selectedCells.length + state.errors)) * 100
        : 100;

      // Save level data
      const levelData: LevelData = {
        patientId: state.patientId,
        sessionId: state.sessionId,
        level: state.currentLevel,
        timestampStart: new Date(Date.now() - state.timeElapsed * 1000).toISOString(),
        timestampEnd: new Date().toISOString(),
        gridSize: state.difficulty.gridSize,
        obstaclesCount: state.difficulty.obstaclesCount,
        moves: state.selectedCells.length,
        errors: state.errors,
        completionTimeSec: state.timeElapsed,
        accuracyPct: accuracy,
        score: score,
        difficultyIndex: state.difficulty.gridSize * (state.difficulty.obstaclesCount + 1),
        ddaAction: score > 0.8 ? 'increase' : score < 0.4 ? 'decrease' : 'maintain'
      };

      // Save to local storage (for backward compatibility)
      storage.saveLevelData(levelData);

      // Save to database via API
      const saveToDatabase = async () => {
        try {
          // Prepare data for API according to NumberSequenceResultDto structure
          const apiData = {
            sessionId: currentSession?.sessionId || state.sessionId, // Use the actual session ID
            timestamp: new Date().toISOString(), // Add required timestamp field
            level: state.currentLevel,
            timestampStart: levelData.timestampStart,
            timestampEnd: levelData.timestampEnd,
            gridSize: state.difficulty.gridSize,
            obstaclesCount: state.difficulty.obstaclesCount,
            movesCount: state.selectedCells.length,
            errorsCount: state.errors,
            completionTimeSeconds: Math.round(state.timeElapsed), // Round to integer
            accuracyPercentage: accuracy,
            score: Math.round(score * 100), // Convert to integer (0-100)
            difficultyIndex: state.difficulty.gridSize * (state.difficulty.obstaclesCount + 1),
            ddaAction: score > 0.8 ? 'increase' : score < 0.4 ? 'decrease' : 'maintain',
            gameData: JSON.stringify({
              moves: state.selectedCells,
              timeElapsed: state.timeElapsed,
              patientId: state.patientId,
              level: state.currentLevel,
              difficulty: state.difficulty
            })
          };
          
          console.log('Saving NumberSequence result to database:', apiData);
          await GameResultSaver.saveNumberSequenceResult(apiData);
          console.log('NumberSequence result saved successfully to database');
        } catch (error) {
          console.error('Failed to save NumberSequence result to database:', error);
          // Don't throw error - game can continue even if database save fails
        }
      };

      saveToDatabase();
      setShowCompletionModal(true);
    }
  }, [state.isComplete, showCompletionModal, isAuthenticated, state.selectedCells.length, state.errors, calculateScore, state.patientId, state.sessionId, state.currentLevel, state.timeElapsed, state.difficulty.gridSize, state.difficulty.obstaclesCount, currentSession, user]);

  const handleNextLevel = () => {
    // Calculate new difficulty using DDA
    const totalNumbers = state.grid.flat().filter(n => n > 0).length;
    const accuracy = state.selectedCells.length > 0
      ? (state.selectedCells.length / (state.selectedCells.length + state.errors)) * 100
      : 100;
    
    const levelData = {
      patientId: state.patientId,
      sessionId: state.sessionId,
      level: state.currentLevel,
      timestampStart: new Date().toISOString(),
      timestampEnd: new Date().toISOString(),
      gridSize: state.difficulty.gridSize,
      obstaclesCount: state.difficulty.obstaclesCount,
      moves: state.selectedCells.length,
      errors: state.errors,
      completionTimeSec: state.timeElapsed,
      accuracyPct: accuracy,
      score: calculateScore(),
      difficultyIndex: state.difficulty.gridSize * (state.difficulty.obstaclesCount + 1),
      ddaAction: 'maintain' as 'increase' | 'decrease' | 'maintain'
    };
    
    // Get new difficulty from DDA
    const newDifficulty = updatePerformance(levelData);
    
    // Update difficulty first, then reset level
    dispatch({ type: 'UPDATE_DIFFICULTY', payload: newDifficulty });
    dispatch({ type: 'RESET_LEVEL' });
    setShowCompletionModal(false);
  };

  const handleReplayLevel = () => {
    // Keep same difficulty when replaying
    dispatch({ type: 'RESET_LEVEL' });
    setShowCompletionModal(false);
  };

  const handleNewSession = () => {
    dispatch({ type: 'RESET_GAME' });
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      localStorage.clear();
      dispatch({ type: 'RESET_GAME' });
    }
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login/register first if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <header className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Cognitive Training Games</h1>
            <p className="text-lg text-gray-600 mb-8">Sign in to start your cognitive training journey</p>
          </header>
          <UserDashboard />
          {showAuthForm && <AuthForm onClose={() => setShowAuthForm(false)} />}
        </div>
      </div>
    );
  }

  // If user is admin, show admin dashboard
  if (isAuthenticated && user?.role === 'admin') {
    return <AdminDashboard />;
  }

  // For authenticated users, show game selector first
  if (isAuthenticated && showGameSelector) {
    return (
      <PatientGameSelector 
        onGameSelect={(selectedGame) => {
          console.log('Game selected:', selectedGame);
          const normalizedGameType = normalizeGameType(selectedGame);
          console.log('Normalized game type:', normalizedGameType);
          setGameType(normalizedGameType);
          setShowGameSelector(false); // Hide selector and show the game
        }}
        currentGame={gameType}
      />
    );
  }

  // If not authenticated, show patient form (fallback)
  if (!state.patientId && !isAuthenticated) {
    return <PatientForm />;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 min-h-screen">
      <header className="mb-4 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Cognitive Training Games</h1>
            <p className="text-sm sm:text-base text-gray-600">
              Welcome {user?.displayName || 'User'} - Age Range: {state.ageRange}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={async () => {
                try {
                  // End current session and go back to game selection
                  if (isSessionActive) {
                    console.log('Ending current session...');
                    await endSession();
                    console.log('Session ended successfully');
                  }
                  // Reset game state
                  dispatch({ type: 'RESET_GAME' });
                  // Show game selector again
                  setShowGameSelector(true);
                } catch (error) {
                  console.error('Error ending session:', error);
                  // Even if session ending fails, still go back to game selector
                  dispatch({ type: 'RESET_GAME' });
                  setShowGameSelector(true);
                }
              }}
              className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
            >
              ← Back to Games
            </button>
            <button
              onClick={logout}
              className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:text-base"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Session Information - Show session details instead of game selection buttons */}
        <div className="mt-4 bg-white rounded-lg shadow-md p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0">
              <div className="text-center sm:text-left">
                <span className="text-xs sm:text-sm text-gray-600">Current Game:</span>
                <span className="ml-0 sm:ml-2 block sm:inline font-semibold text-blue-600 text-sm sm:text-base">
                  {gameType === 'sequence' ? 'Number Sequencing' :
                   gameType === 'memory' ? 'Memory Palace' :
                   gameType === 'tone' ? 'Tone Memory' :
                   gameType === 'matching' ? 'Image Matching' :
                   gameType === 'ticker' ? 'Ticker Tape Rush' : 'Game'}
                </span>
              </div>
              {currentSession && (
                <>
                  <div className="text-center sm:text-left">
                    <span className="text-xs sm:text-sm text-gray-600">Session #:</span>
                    <span className="ml-0 sm:ml-2 block sm:inline font-semibold text-green-600 text-sm sm:text-base">{currentSession.sessionId.substring(0, 8)}</span>
                  </div>
                  <div className="text-center sm:text-left">
                    <span className="text-xs sm:text-sm text-gray-600">Session ID:</span>
                    <span className="ml-0 sm:ml-2 block sm:inline font-mono text-xs text-gray-500">{currentSession.sessionId.substring(0, 8)}...</span>
                  </div>
                  <div className="text-center sm:text-left">
                    <span className="text-xs sm:text-sm text-gray-600">Started:</span>
                    <span className="ml-0 sm:ml-2 block sm:inline text-xs sm:text-sm text-gray-500">
                      {new Date(currentSession.startTime).toLocaleTimeString()}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-center sm:justify-end">
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                Active Session
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        {gameType === 'sequence' ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-8">
            <div className="lg:col-span-3 order-2 lg:order-1">
              <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-md mx-2 sm:mx-0">
                <div className="flex justify-center items-center min-h-[300px] sm:min-h-[400px]">
                  <Grid />
                </div>
              </div>
              
              <div className="mt-4 sm:mt-8 mx-2 sm:mx-0">
                <ResultsDashboard />
              </div>
            </div>
            
            <div className="lg:col-span-1 order-1 lg:order-2 mx-2 sm:mx-0">
              <HUD />
            </div>
          </div>
        ) : gameType === 'memory' ? (
          <MemoryGame />
        ) : gameType === 'tone' ? (
          <ToneGameProvider>
            <ToneGame />
          </ToneGameProvider>
        ) : gameType === 'matching' ? (
          <MatchingGridProvider>
            <MatchingGrid />
          </MatchingGridProvider>
        ) : gameType === 'ticker' ? (
          <TickerTapeGame />
        ) : (
          // Default to game selector if no game matches
          <div className="text-center py-8">
            <p>Game type not recognized: {gameType}</p>
            <button 
              onClick={() => setShowGameSelector(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Back to Games
            </button>
          </div>
        )}
      </main>

      {showCompletionModal && (
        <CompletionModal
          onNextLevel={handleNextLevel}
          onReplayLevel={handleReplayLevel}
        />
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <GameSessionProvider>
        <GameProvider>
          <div className="min-h-screen bg-gray-100">
            <GameContent />
          </div>
        </GameProvider>
      </GameSessionProvider>
    </AuthProvider>
  );
};

export default App;
